import React, { useCallback, useContext, useEffect, useState } from 'react'

/**
 * Motyw jasny / ciemny.
 *
 * Stan sprowadza się do jednej klasy `dark` na <html> — od niej zależą wszystkie
 * zmienne kolorów z `src/index.css`, a przez nie każda klasa Tailwinda. React
 * niczego tu nie maluje, tylko przestawia klasę.
 *
 * Pierwsze ustawienie klasy robi skrypt w `public/index.html`, PRZED renderem —
 * inaczej przy każdym wejściu mignąłby biały ekran. Ten plik tylko przejmuje
 * stan, który tam powstał, więc obie ścieżki muszą używać tego samego klucza
 * `KLUCZ` i tej samej reguły wyboru.
 *
 * Reguła: **domyślnie motyw JASNY**. Ciemny włącza się wyłącznie wtedy, gdy
 * użytkownik sam go wybrał (zapis w localStorage). Świadomie NIE idziemy za
 * `prefers-color-scheme` — sporo osób ma ciemny system, a nie chce ciemnej
 * strony, i wchodziło tu od razu na ciemne tło, nie prosząc o to.
 */

const KLUCZ = 'vv-motyw'

// Kolor paska adresu na telefonie. Jasny = kolor nawigacji (biały), ciemny =
// kolor tła strony (`--vv-strona`).
const PASEK_PRZEGLADARKI = { light: '#ffffff', dark: '#10151f' }

const ThemeContext = React.createContext({
  motyw: 'light',
  ciemny: false,
  przelaczMotyw: () => {},
  ustawMotyw: () => {},
})

export const ThemeProvider = ({ children }) => {
  // Źródłem prawdy przy starcie jest to, co ustawił skrypt z index.html —
  // czytamy klasę, a nie localStorage. Gdyby oba źródła się rozjechały (np. inna
  // karta zmieniła zapis), liczy się to, co użytkownik faktycznie widzi.
  const [motyw, setMotyw] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light'
  )

  useEffect(() => {
    const korzen = document.documentElement
    korzen.classList.toggle('dark', motyw === 'dark')

    // `color-scheme` mówi przeglądarce, jak malować rzeczy, których nie stylujemy:
    // rozwijane listy `<select>`, pola daty, domyślne paski przewijania.
    korzen.style.colorScheme = motyw

    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', PASEK_PRZEGLADARKI[motyw])
  }, [motyw])

  // Wydruk zawsze w motywie jasnym. Papier jest biały, a jedyna rzecz, którą
  // ten serwis drukuje, to schemat elektryczny dla instalatora („Pobierz PDF"
  // w oknie schematu) — ciemny motyw dałby tam czarną stronę albo, przy
  // wyłączonych tłach w przeglądarce, jasny tekst na białym tle.
  useEffect(() => {
    if (motyw !== 'dark') return
    const korzen = document.documentElement
    const przedDrukiem = () => {
      korzen.classList.remove('dark')
      korzen.style.colorScheme = 'light'
    }
    const poDruku = () => {
      korzen.classList.add('dark')
      korzen.style.colorScheme = 'dark'
    }
    window.addEventListener('beforeprint', przedDrukiem)
    window.addEventListener('afterprint', poDruku)
    return () => {
      window.removeEventListener('beforeprint', przedDrukiem)
      window.removeEventListener('afterprint', poDruku)
    }
  }, [motyw])

  const ustawMotyw = useCallback((nowy) => {
    try {
      localStorage.setItem(KLUCZ, nowy)
    } catch (e) {
      // Brak zapisu = motyw zostaje na czas tej sesji. Świadomie nie blokujemy
      // przełącznika, bo sam przełącznik działa również bez localStorage.
    }

    // Przejście kolorów tylko na moment przełączenia — reguła `.vv-zmiana-motywu`
    // w index.css. Na stałe rozmazywałaby każdy hover na listach produktów.
    const korzen = document.documentElement
    korzen.classList.add('vv-zmiana-motywu')
    window.setTimeout(() => korzen.classList.remove('vv-zmiana-motywu'), 250)

    setMotyw(nowy)
  }, [])

  const przelaczMotyw = useCallback(
    () => ustawMotyw(motyw === 'dark' ? 'light' : 'dark'),
    [motyw, ustawMotyw]
  )

  return (
    <ThemeContext.Provider value={{ motyw, ciemny: motyw === 'dark', przelaczMotyw, ustawMotyw }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)

export default ThemeContext
